"use client"
import { SearchBar } from '@/components/ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Text,
  TextField
} from '@radix-ui/themes';
import { Filter, Plus } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

export interface FAQFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedMapping: string;
  selectedDateRange: string;
  selectedAuthor: string;
  dateFrom?: string;
  dateTo?: string;
  blogSlug?: string;
  contentType?: 'blogs' | 'thought-leadership';
}
const statuses = ['All', 'Active', 'Inactive'];
const mappingOptions = ['All', 'Brand Development Service', 'Logo Design Service', 'Rebranding Service', 'AI as a Service'];
const dateRanges = ['All Time', 'Today', 'This Week', 'This Month', 'Last 3 Months'];

interface FAQFiltersProps {
  onFiltersChange: (filters: FAQFilters) => void;
  onClearFilters?: () => void;
}

export default function FAQFilters({ onFiltersChange, onClearFilters }: FAQFiltersProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedMapping, setSelectedMapping] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  const [selectedAuthor, setSelectedAuthor] = useState('All');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [authors, setAuthors] = useState<string[]>(['All']);

  // Load authors from user management (Author/Editor/Admin with active refreshToken)
  const loadAuthors = async () => {
    try {
      const response = await fetch('/api/admin/users');
      if (response.ok) {
        const data = await response.json();
        if (data.users && Array.isArray(data.users)) {
          const authorNames = data.users
            .filter((user: { refreshToken?: string; role: string; name: string }) =>
              user.refreshToken &&
              ['Author', 'Editor', 'Admin'].includes(user.role)
            )
            .map((user: { name: string }) => user.name)
            .filter((name: string) => name && name.trim().length > 0);

          setAuthors(['All', ...authorNames]);
        }
      }
    } catch {
      // Fall back to just "All" if API fails
    }
  };

  useEffect(() => {
    loadAuthors();
  }, []);

  // Memoize filters object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => ({
    searchTerm,
    selectedStatus,
    selectedMapping,
    selectedAuthor,
    selectedDateRange,
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  }), [searchTerm, selectedStatus, selectedMapping, selectedAuthor, selectedDateRange, dateFrom, dateTo]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange(memoizedFilters);
  }, [memoizedFilters, onFiltersChange]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('All');
    setSelectedMapping('All');
    setSelectedAuthor('All');
    setSelectedDateRange('All Time');
    setDateFrom('');
    setDateTo('');
    onClearFilters?.();
  };

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <Box className="space-y-4">
        {/* Main Search */}
        <Flex align="center" gap="4">
          <Box className="flex-1 max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search FAQs by question, answer, or keywords..."
            />
          </Box>

          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>

          <Link href="/admin/faqs/new">
            <Button>
              <Plus className="h-4 w-4" />
              Create FAQ
            </Button>
          </Link>
        </Flex>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-14 gap-4 pt-4 border-t border-gray-200">
            {/* Status Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Status
              </Text>
              <Select.Root value={selectedStatus} onValueChange={setSelectedStatus}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {statuses.map(status => (
                    <Select.Item key={status} value={status}>
                      {status}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Mapping Filter */}
            <Box className='col-span-3'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Mapped To
              </Text>
              <Select.Root value={selectedMapping} onValueChange={setSelectedMapping}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {mappingOptions.map(option => (
                    <Select.Item key={option} value={option}>
                      {option}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Author Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Author
              </Text>
              <Select.Root value={selectedAuthor} onValueChange={setSelectedAuthor}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {authors.map(author => (
                    <Select.Item key={author} value={author}>
                      {author}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Date Range Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Date Range
              </Text>
              <Select.Root value={selectedDateRange} onValueChange={setSelectedDateRange}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {dateRanges.map(range => (
                    <Select.Item key={range} value={range}>
                      {range}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Custom Date Range */}
            <Box className='col-span-5'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Custom Range
              </Text>
              <Flex gap="2">
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Flex>
            </Box>
          </div>
        )}

        {/* Active Filters Display */}
        {(selectedStatus !== 'All' || selectedMapping !== 'All' || selectedAuthor !== 'All' || selectedDateRange !== 'All Time') && (
          <Flex align="center" gap="2" className="pt-4 border-t border-gray-200">
            <Text size="2" className="text-gray-500">Active filters:</Text>
            {selectedStatus !== 'All' && (
              <Badge variant="soft" color="green">
                Status: {selectedStatus}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedStatus('All')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedAuthor !== 'All' && (
              <Badge variant="soft" color="blue">
                Author: {selectedAuthor}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedAuthor('All')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedMapping !== 'All' && (
              <Badge variant="soft" color="purple">
                Mapped To: {selectedMapping}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedMapping('All')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedDateRange !== 'All Time' && (
              <Badge variant="soft" color="orange">
                Date: {selectedDateRange}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedDateRange('All Time')}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="1"
              color="red"
              onClick={clearFilters}
            >
              Clear all
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
